<script>
import RawData from "@/components/widgets/raw-data.vue";
export default {
    name: "SubsidiaryLedgerTable",
    components: {
        RawData,
    },
    props: {
        subsidiaryLedgers: {
            type: Array,
            default: () => [],
        },
        slTypes: {
            type: Array,
            default: () => [],
        },
        filter: {
            type: String,
            default: null,
        },
        perPage: {
            type: Number,
            default: 10,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentPage: 1,
            sortBy: null,
            sortDesc: false,
            fields: [
                {
                    key: "code",
                    label: "Code",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center font-weight-bold",
                },
                {
                    key: "slType",
                    label: "SL Type",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center",
                },
                {
                    key: "client",
                    label: "Client/Employee/Property",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "",
                },
                {
                    key: "title",
                    label: "Title/Project Name",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "",
                },
                {
                    key: "contractNo",
                    label: "Contract No",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center",
                },
                {
                    key: "validity",
                    label: "Validity",
                    sortable: true,
                    thClass: "text-center",
                    tdClass: "text-center",
                },
                {
                    key: "actions",
                    label: "Actions",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center",
                    width: "120px",
                },
            ],
        };
    },
    computed: {
        filtered() {
            let data = this.subsidiaryLedgers;
            if (this.filter && this.filter.trim() !== "") {
                data = data.filter((item) => {
                    return item.filterKey
                        .toLowerCase()
                        .includes(this.filter.toLowerCase());
                });
            }
            return data;
        },
        processedData() {
            return this.filtered.map(item => ({
                ...item,
            }));
        },
        rows() {
            return this.filtered.length;
        },
    },
    methods: {
        formatValidity(validityDate) {
            if (!validityDate) return '-';
            try {
                const date = new Date(validityDate);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit'
                });
            } catch (error) {
                return '-';
            }
        },
        editLedger(item) {
            this.$emit("edit-ledger", item);
        },
        deleteLedger(item) {
            this.$emit("delete-ledger", item);
        },
        viewLedger(item) {
            this.$emit("view-ledger", item);
        },
    },
};
</script>

<template>
    <div class="subsidiary-ledger-table">
        <template v-if="loading">
            <div class="table-responsive">
                <table class="table table-bordered table-hover mb-0">
                    <thead class="table-light">
                        <tr>
                            <th class="text-center" v-for="field in fields" :key="field.key">
                                <b-skeleton width="80%" height="20px"></b-skeleton>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="i in 10" :key="i">
                            <td v-for="field in fields" :key="field.key" class="text-center">
                                <b-skeleton width="90%" height="25px"></b-skeleton>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <template v-else-if="!subsidiaryLedgers.length">
            <div class="text-center py-5">
                <div class="mb-3">
                    <i class="bx bx-folder-open font-size-48 text-muted"></i>
                </div>
                <h4 class="text-muted">No Subsidiary Ledgers Found</h4>
                <p class="text-muted">Create your first subsidiary ledger to get started.</p>
            </div>
        </template>

        <template v-else>
            <div class="table-responsive">
                <b-table
                    :items="processedData"
                    :fields="fields"
                    :current-page="currentPage"
                    :per-page="perPage"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    class="table-bordered table-hover mb-0"
                    thead-class="table-light"
                    tbody-tr-class="align-middle"
                    show-empty
                    empty-text="No subsidiary ledgers found"
                    hover
                >
                    <template #cell(code)="data">
                        <div class="position-relative">
                            <RawData :data="data.item" v-if="$store.state.decrypt.isAdmin()" />
                            <span class="badge text-primary font-size-12">{{ data.item.code || '-' }}</span>
                        </div>
                    </template>

                    <template #cell(slType)="data">
                        <span class="badge bg-info font-size-12">{{ data.item.subsidiaryLedgerType }}</span>
                    </template>

                    <template #cell(client)="data">
                        <div class="text-truncate" :title="data.item.client">
                            {{ data.item.clientDto ? data.item.clientDto.name : '-' }}
                        </div>
                    </template>

                    <template #cell(projectName)="data">
                        <div class="text-truncate" style="max-width: 250px;" :title="data.item.projectName">
                            {{ data.item.projectName || '-' }}
                        </div>
                    </template>

                    <template #cell(contractNo)="data">
                        <span class="font-weight-bold">{{ data.item.contractNumber || '-' }}</span>
                    </template>

                    <template #cell(validity)="data">
                        <div class="d-flex flex-column align-items-center">
                            <span>{{ formatDateShort(new Date(data.item.validFromDate)) }} - {{ formatDateShort(new Date(data.item.validToDate)) }}</span>
                        </div>
                    </template>

                    <template #cell(actions)="data">
                        <div class="d-flex justify-content-center gap-1">
                            <b-button
                                size="sm"
                                variant="outline-info"
                                @click="viewLedger(data.item)"
                                title="View General Ledger"
                            >
                                <i class="bx bx-book-open font-size-14"></i>
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                @click="editLedger(data.item)"
                                title="Edit"
                            >
                                <i class="bx bx-edit font-size-14"></i>
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-danger"
                                @click="deleteLedger(data.item)"
                                title="Delete"
                            >
                                <i class="bx bx-trash font-size-14"></i>
                            </b-button>
                        </div>
                    </template>
                </b-table>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-content-between align-items-center mt-3" v-if="rows > perPage">
                <div class="text-muted font-size-14">
                    Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, rows) }} of {{ rows }} entries
                </div>
                <div class="dataTables_paginate paging_simple_numbers">
                    <ul class="pagination pagination-rounded mb-0">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="rows"
                            :per-page="perPage"
                            size="sm"
                            class="mb-0"
                        ></b-pagination>
                    </ul>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped>
.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.table-hover tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.05);
}

.badge {
    font-size: 0.75rem;
}

.font-size-10 {
    font-size: 0.625rem;
}

.font-size-12 {
    font-size: 0.75rem;
}

.font-size-14 {
    font-size: 0.875rem;
}

.font-size-48 {
    font-size: 3rem;
}
</style>